import React from 'react';
import { Link } from 'react-router-dom';
import { Building2, ShieldCheck, ArrowRight, Sparkles } from 'lucide-react';

export default function Landing() {
  return (
    <div className="min-h-screen bg-slate-50 gradient-mesh">
      <header className="px-8 py-6 flex items-center justify-between border-b border-slate-200/70 bg-white/70 backdrop-blur">
        <div className="flex items-center gap-2.5">
          <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-teal-500 to-teal-700 text-white grid place-items-center font-bold text-[13px] shadow-lg shadow-teal-900/20">SE</div>
          <div>
            <div className="font-semibold text-slate-900 leading-tight tracking-tight text-[15px]">StayEasy</div>
            <div className="text-[10.5px] text-slate-500 leading-tight uppercase tracking-[0.09em] font-medium mt-0.5">Console</div>
          </div>
        </div>
        <div className="text-[13px] text-slate-500 font-mono">console.stayeasy.in</div>
      </header>

      <main className="max-w-6xl mx-auto px-8 py-20 md:py-28">
        <div className="max-w-2xl">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-teal-50 text-teal-700 text-[11px] font-semibold border border-teal-100 mb-7 tracking-tight">
            <Sparkles className="h-3 w-3" strokeWidth={2.5} /> New unified console
          </div>
          <h1 className="text-[52px] md:text-[68px] font-semibold text-slate-900 leading-[0.98] tracking-[-0.038em] text-balance">
            Run your stays.<br />
            <span className="font-serif italic font-normal text-slate-700">Grow</span> your platform.
          </h1>
          <p className="mt-6 text-[17px] text-slate-500 max-w-xl leading-relaxed text-pretty">
            The StayEasy console gives hosts everything they need to manage properties and bookings, and gives our team the tools to run the marketplace with confidence.
          </p>
        </div>

        <div className="mt-14 grid md:grid-cols-2 gap-5">
          <Link to="/host/login" className="group relative overflow-hidden rounded-2xl bg-white border border-slate-200/80 p-8 card-hover">
            <div className="absolute top-0 right-0 h-40 w-40 rounded-full bg-teal-100/60 blur-3xl -translate-y-10 translate-x-10 group-hover:translate-x-5 transition-transform duration-500" />
            <div className="relative">
              <div className="h-11 w-11 rounded-xl bg-teal-100 text-teal-700 grid place-items-center mb-6 ring-1 ring-teal-200/60">
                <Building2 className="h-5 w-5" strokeWidth={2} />
              </div>
              <div className="text-[10.5px] uppercase tracking-[0.12em] text-teal-700 font-semibold">For hosts</div>
              <h3 className="mt-1.5 text-[24px] font-semibold text-slate-900 tracking-[-0.02em]">Host Console</h3>
              <p className="mt-2 text-slate-500 text-[13.5px] leading-relaxed">
                Manage properties, bookings, guests, reviews and payouts — all in one place.
              </p>
              <div className="mt-6 inline-flex items-center gap-1.5 text-teal-700 font-semibold text-[13px]">
                Sign in as host
                <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-1 transition-transform" strokeWidth={2.25} />
              </div>
            </div>
          </Link>

          <Link to="/admin/login" className="group relative overflow-hidden rounded-2xl bg-slate-950 text-white p-8 card-hover ring-1 ring-slate-900">
            <div className="absolute top-0 right-0 h-40 w-40 rounded-full bg-amber-400/20 blur-3xl -translate-y-10 translate-x-10 group-hover:translate-x-5 transition-transform duration-500" />
            <div className="relative">
              <div className="h-11 w-11 rounded-xl bg-white/[0.06] text-amber-300 grid place-items-center mb-6 ring-1 ring-white/10">
                <ShieldCheck className="h-5 w-5" strokeWidth={2} />
              </div>
              <div className="text-[10.5px] uppercase tracking-[0.12em] text-amber-300 font-semibold">For platform team</div>
              <h3 className="mt-1.5 text-[24px] font-semibold tracking-[-0.02em]">Admin Console</h3>
              <p className="mt-2 text-slate-400 text-[13.5px] leading-relaxed">
                Moderate hosts, oversee bookings, manage finance and monitor platform operations.
              </p>
              <div className="mt-6 inline-flex items-center gap-1.5 text-amber-300 font-semibold text-[13px]">
                Sign in as admin
                <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-1 transition-transform" strokeWidth={2.25} />
              </div>
            </div>
          </Link>
        </div>

        <div className="mt-16 text-[12px] text-slate-500">
          Backend endpoints are served by <code className="px-1.5 py-0.5 rounded-md bg-slate-100 text-slate-700 font-mono text-[11px]">api.stayeasy.io</code>. This console is UI-only during preview.
        </div>
      </main>
    </div>
  );
}
